import axios, { AxiosError, AxiosResponse, Canceler } from 'axios';
import { Buffer } from 'buffer';
import { message } from 'antd';

/**
 * Timeout in milliseconds defaults to 15 seconds
 */
const DEFAULT_TIMEOUT = 1000 * 15;

/**
 * Timeout in milliseconds defaults to 20 minutes
 */
const UPLOAD_FILE_TIMEOUT = 1000 * 60 * 20;

/**
 * Post Timeout in milliseconds defaults to 5 minute
 * temporary for import prev agenda endpoint 
 */
const POST_TIMEOUT = 1000 * 60 * 5;

const instance = axios.create({
    baseURL: '/api/',
    timeout: DEFAULT_TIMEOUT
});

// Malware serve-gate (D11): mirror govenda-client / govenda-admin. When the API blocks a file it returns
// 409 (Pending) / 403 (Quarantined) with an X-Scan-Status header; surface a friendly message and flag the
// error as scanBlocked so callers can suppress their own generic error handling.
instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        const scanStatus = (error?.response?.headers as any)?.["x-scan-status"];
        if (scanStatus === "Quarantined") {
            message.error("This file was flagged as malicious and has been quarantined, so it can't be opened.", 8);
            (error as any).scanBlocked = true;
        } else if (scanStatus === "Pending") {
            message.warning("This file is still being scanned for malware and isn't available yet. Please try again shortly.", 6);
            (error as any).scanBlocked = true;
        }
        return Promise.reject(error);
    },
);

const cancelToken = axios.CancelToken;

const responseBody = (response: AxiosResponse) => response.data;
const errorBody = (error: AxiosError) => error.response;
const fileResponse = (response: AxiosResponse) => { response.data = Buffer.from(response.data, "binary").toString("base64"); return response; }
const fileResponseBody = (response: AxiosResponse) => Buffer.from(response.data, "binary").toString("base64");

export const PARTNER_ID = 1;
export const APP_APPLICATION_ID = 1;
export const ADMIN_APPLICATION_ID = 2;
export const CSPORTAL_APPLICATION_ID = 5;
export const CSPORTAL_APPLICATION_STRING = "CS Portal";
export const HIDE_SHARED_FOLDER = true;

export let cancel: Canceler;

export const api = {
    get: (url: string, apiVersion:string = "1.0", timeoutMs:number = UPLOAD_FILE_TIMEOUT) => instance.get(url, { headers: { "api-version": apiVersion }, cancelToken: new cancelToken((c) => cancel = c), timeout: timeoutMs}).then(responseBody),
    post: (url: string, body: {}, apiVersion:string = "1.0", timeoutMs:number = POST_TIMEOUT) => instance.post(url, body, { headers: { 'Content-type': 'application/json', 'api-version' : apiVersion }, cancelToken: new cancelToken((c) => cancel = c), timeout: timeoutMs}).then(responseBody),
    loginPost: (url: string, body: {}, apiVersion:string = "1.0", timeoutMs:number = POST_TIMEOUT) => instance.post(url, body, { headers: { 'Content-type': 'application/json', 'api-version' : apiVersion }, cancelToken: new cancelToken((c) => cancel = c), timeout: timeoutMs}).then(responseBody).catch(errorBody),
    put: (url: string, body: {}, apiVersion:string = "1.0", timeoutMs:number = DEFAULT_TIMEOUT) => instance.put(url, body, { headers: { 'Content-type': 'application/json', 'api-version': apiVersion }, timeout: timeoutMs}).then(responseBody),
    delete: (url: string, body: {}, apiVersion:string = "1.0", timeoutMs:number = DEFAULT_TIMEOUT) => instance.delete(url, { data: body, headers: { 'Content-type': 'application/json', 'api-version': apiVersion }, timeout: timeoutMs}).then(responseBody),
    patch: (url: string, body: {}, apiVersion:string = "1.0", timeoutMs:number = DEFAULT_TIMEOUT) => instance.patch(url, body, { headers: { 'Content-type': 'application/json', 'api-version': apiVersion }, timeout: timeoutMs}).then(responseBody),
    /**
     * for patching with data and file
     * @param url 
     * @param body 
     * @param apiVersion 
     * @returns response data
     */
    patchMultiPart: (url: string, body: {}, apiVersion:string = "1.0", timeoutMs:number = UPLOAD_FILE_TIMEOUT) => instance.patch(url, body, { headers: { 'Content-type': 'multipart/form-data', 'api-version': apiVersion }, timeout: timeoutMs}).then(responseBody),
    /**
     * for posting with data and file
     * @param url 
     * @param body 
     * @param apiVersion 
     * @returns response data
     */
    postMultiPart: (url: string, body: {}, apiVersion:string = "1.0", timeoutMs:number = UPLOAD_FILE_TIMEOUT) => instance.post(url, body, { headers: { 'Content-type': 'multipart/form-data', 'api-version' : apiVersion }, timeout: timeoutMs}).then(responseBody),
    /**
     * for putting with data and file
     * @param url 
     * @param body 
     * @param apiVersion 
     * @returns response data
     */
    putMultiPart: (url: string, body: {}, apiVersion:string = "1.0", timeoutMs:number = UPLOAD_FILE_TIMEOUT) => instance.put(url, body, { headers: { 'Content-type': 'multipart/form-data', 'api-version': apiVersion }, timeout: timeoutMs}).then(responseBody),
    /**
     * get file from server
     * @param url 
     * @param apiVersion 
     * @returns base64
     */
    getFile: (url: string, apiVersion:string = "1.0", timeoutMs:number = UPLOAD_FILE_TIMEOUT) => instance.get(url, { responseType: 'arraybuffer',  headers: { "api-version": apiVersion }, cancelToken: new cancelToken((c) => cancel = c), timeout: timeoutMs}).then(fileResponseBody),
    /**
     * get file from server
     * @param url 
     * @param apiVersion 
     * @returns array buffer
     */
    getFileBuffer: (url: string, apiVersion:string = "1.0", timeoutMs:number = UPLOAD_FILE_TIMEOUT) => instance.get(url, { responseType: 'arraybuffer',  headers: { "api-version": apiVersion }, cancelToken: new cancelToken((c) => cancel = c), timeout: timeoutMs}).then(responseBody),
    /**
     * get file from server using a post request
     * @param url
     * @param body
     * @param apiVersion 
     * @returns array buffer
     */
    postGetFile: (url: string, body: {}, apiVersion:string = "1.0", timeoutMs:number = UPLOAD_FILE_TIMEOUT) => instance.post(url, body, { responseType: 'arraybuffer',  headers: { "api-version": apiVersion }, cancelToken: new cancelToken((c) => cancel = c), timeout: timeoutMs}).then(fileResponse)
};

